import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

// Decode JWT payload to check expiry
function getTokenExpiry(jwt) {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('aldealix_token'));
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false); // se resetea al cerrar sesión
  const expiryTimer = useRef(null);

  const verify = () => setVerified(true);

  const logout = useCallback(() => {
    localStorage.removeItem('aldealix_token');
    setToken(null);
    setUser(null);
    setVerified(false); // reset verificación
    if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
  }, []);

  // Auto-logout when JWT expires
  useEffect(() => {
    if (!token) return;
    const exp = getTokenExpiry(token);
    if (!exp) return;
    const msUntilExpiry = exp - Date.now();
    if (msUntilExpiry <= 0) { logout(); return; }
    expiryTimer.current = setTimeout(() => { logout(); }, msUntilExpiry);
    return () => { if (expiryTimer.current) clearTimeout(expiryTimer.current); };
  }, [token, logout]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.me(token)
      .then(data => setUser(data.user))
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token, logout]);

  const login = (jwtToken, userData) => {
    localStorage.setItem('aldealix_token', jwtToken);
    setToken(jwtToken);
    setUser(userData);
  };

  const refreshUser = async () => {
    if (!token) return;
    const data = await api.me(token);
    setUser(data.user);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser, verified, verify }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
